import { makeAutoObservable } from 'mobx';
import { OrderForWorklist } from '@/Models/GenericWorklist/WorklistResult';
import { remarks } from '@/API';
import { OrderRemark } from '@/Models/GenericWorklist/Remarks';

export interface IWorklistDataGridStore {
    showRemarksModalIsOpen: boolean;
    isLoadingRemarks: boolean;
    orderID: number;
    selectedOrderRemarks?: OrderRemark;
    showJustificationModalIsOpen: boolean;
    selectedOrderForJustification?: OrderForWorklist;
    showReportModalIsOpen: boolean;
    selectedOrderForReportPopup?: OrderForWorklist;
    selectedReportPopupProcedureIds?: number[];
    selectedReportPopupReportId?: number;
}

export default class WorklistDataGridStore implements IWorklistDataGridStore {
    showRemarksModalIsOpen = false;
    isLoadingRemarks = false;
    orderID = 0;
    selectedOrderRemarks = {} as OrderRemark;
    private abortController?: AbortController;
    showJustificationModalIsOpen = false;
    selectedOrderForJustification?: OrderForWorklist;
    showReportModalIsOpen = false;
    selectedOrderForReportPopup?: OrderForWorklist;
    selectedReportPopupProcedureIds?: number[];
    selectedReportPopupReportId?: number;

    constructor() {
        makeAutoObservable(this);
    }

    setOrder = async (order: OrderForWorklist): Promise<void> => {
        // Cancel any previous request
        this.abortController?.abort();

        // Create new AbortController for this request
        this.abortController = new AbortController();
        const currentAbortController = this.abortController;

        this.setLoadingRemarks(true);
        try {
            const patientRemark = await remarks.getRemarksForWorklist(order.PatientInfo.ID, currentAbortController.signal);

            // Check if this request was cancelled
            if (currentAbortController.signal.aborted) {
                return;
            }

            const orderRemarks: OrderRemark = {
                PatientRemark: {
                    Allergies: patientRemark.Allergies,
                    Particularities: patientRemark.Particularities,
                    LastName: order.PatientInfo.LastName,
                    FirstName: order.PatientInfo.FirstName ?? '',
                    Private: order.PatientInfo.PatientRemarksPrivate ?? '',
                    Public: order.PatientInfo.PatientRemarksPublic ?? '',
                },
                OrderNumber: order.OrderInfo.OrderNumber,
                OrderRemarks: order.OrderInfo.OrderRemarks ?? '',
                RequestingName: order.OrderInfo.RequestingPhysician,
                RequestingPrivate: order.OrderInfo.RequestingPhysicianRemarksPrivate ?? '',
                RequestingPublic: order.OrderInfo.RequestingPhysicianRemarksPublic ?? '',
            };

            this.setSelectedOrderRemarks(orderRemarks);
            this.setShowRemarksModalIsOpen(true);
        }
        catch (error) {
            // Silently ignore aborted requests
            if (error instanceof Error && error.name === 'AbortError') {
                return;
            }

            console.error('Failed to load patient remarks:', error);
            this.showRemarksModalIsOpen = false;
        }
        finally {
            this.setLoadingRemarks(false);
        }
    };

    setModalNotVisible = () => {
        // Cancel any in-flight request
        this.abortController?.abort();
        this.abortController = undefined;

        this.showRemarksModalIsOpen = false;
        this.selectedOrderRemarks = {} as OrderRemark;
    };

    setShowRemarksModalIsOpen = (isOpen: boolean) => {
        this.showRemarksModalIsOpen = isOpen;
    };

    setSelectedOrderRemarks = (remarks: OrderRemark) => {
        this.selectedOrderRemarks = remarks;
    };

    setLoadingRemarks = (loading: boolean) => {
        this.isLoadingRemarks = loading;
    };

    setJustificationOrder = (order: OrderForWorklist) => {
        this.selectedOrderForJustification = order;
        this.setShowJustificationModalIsOpen(true);
    };

    setJustificationModalNotVisible = () => {
        this.showJustificationModalIsOpen = false;
        this.selectedOrderForJustification = undefined;
    };

    setShowJustificationModalIsOpen = (isOpen: boolean) => {
        this.showJustificationModalIsOpen = isOpen;
    };

    setReportOrder = (order: OrderForWorklist, reportId: number, reportProcedureIds?: number[]) => {
        this.selectedOrderForReportPopup = order;
        this.selectedReportPopupReportId = reportId;
        this.selectedReportPopupProcedureIds = reportProcedureIds;
        this.setShowReportModalIsOpen(true);
    };

    setReportModalNotVisible = () => {
        this.showReportModalIsOpen = false;
        this.selectedOrderForReportPopup = undefined;
        this.selectedReportPopupProcedureIds = undefined;
        this.selectedReportPopupReportId = undefined;
    };

    setShowReportModalIsOpen = (isOpen: boolean) => {
        this.showReportModalIsOpen = isOpen;
    };
}
